<script lang="ts">
  /**
   * The small capitals over a block (the eyebrow step). It sits directly on
   * the ground, never inside a card, at whichever heading level the page's
   * outline needs (`as`), or as plain text when it only labels what is under
   * it. TwinTen's tab, picker and sheet labels.
   *
   * - `page`: over a block on a tab (Home's "Continue a game").
   * - `picker`: the same, spaced as a picker's question (more room above and
   *   below).
   * - `sheet`: between the groups of a full page of rows (lighter, tracked
   *   wider, indented to the rows' own text).
   *
   * The words are typed in sentence case; the capitals are CSS.
   *
   * Test id: `testid`. No default.
   */
  import type { Snippet } from 'svelte';

  interface Props {
    readonly children: Snippet;
    readonly as?: 'h2' | 'h3' | 'p' | 'span';
    /** An id, so the block under it can be `aria-labelledby` this label. */
    readonly id?: string | undefined;
    readonly variant?: 'page' | 'picker' | 'sheet';
    readonly testid?: string | undefined;
  }
  const { children, as = 'p', id, variant = 'page', testid }: Props = $props();
</script>

<svelte:element
  this={as}
  {id}
  class="ui-section-label ui-section-label--{variant}"
  data-testid={testid}
>
  {@render children()}
</svelte:element>

<style>
  .ui-section-label {
    display: block;
    margin: 0.25rem 0.25rem 0;
    font: var(--ui-text-eyebrow);
    letter-spacing: var(--ui-tracking-eyebrow);
    text-transform: uppercase;
    color: var(--ui-ink-2);
  }

  .ui-section-label--picker {
    margin: 0.75rem 0.25rem 0.5rem;
  }

  .ui-section-label--sheet {
    margin: 1rem 1.25rem 0.35rem;
    font-weight: 700;
    letter-spacing: 0.1em;
  }
</style>
